import React, { useState } from 'react';
import { LL97Service } from '../services/ll97Service';

const LL97Calculator: React.FC = () => {
    const [buildingType, setBuildingType] = useState<string>('Office');
    const [grossFloorArea, setGrossFloorArea] = useState<number>(100000);
    const [totalGHGEmissions, setTotalGHGEmissions] = useState<number>(500);
    const [currentCalculation, setCurrentCalculation] = useState<any>(null);
    const [futureCalculation, setFutureCalculation] = useState<any>(null);

    const handleCalculate = () => {
        console.log('Calculate button clicked!');
        console.log('Building Type:', buildingType);
        console.log('Gross Floor Area:', grossFloorArea);
        console.log('Total GHG Emissions:', totalGHGEmissions);
        
        try {
            const current = LL97Service.calculateLL97Fine(buildingType, grossFloorArea, totalGHGEmissions, 2025);
            const future = LL97Service.calculateLL97Fine(buildingType, grossFloorArea, totalGHGEmissions, 2030);
            
            console.log('Current calculation result:', current);
            console.log('Future calculation result:', future);
            
            // Ensure we have valid results before setting state
            if (current && typeof current === 'object') {
                setCurrentCalculation(current);
                console.log('Current calculation set successfully');
            } else {
                console.error('Invalid current calculation result:', current);
            }
            
            if (future && typeof future === 'object') {
                setFutureCalculation(future);
                console.log('Future calculation set successfully');
            } else {
                console.error('Invalid future calculation result:', future);
            }
        } catch (error) {
            console.error('Error in calculation:', error);
            alert('Error calculating LL97 fines. Please check your inputs and try again.');
        }
    };

    const buildingTypes = [
        'Office',
        'Multifamily Housing',
        'Retail',
        'Hotel',
        'Hospital',
        'Mixed Use'
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-gray-100 py-8">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Professional Header */}
                <div className="text-center mb-12">
                    <h1 className="text-5xl font-bold bg-gradient-to-r from-primary-600 via-secondary-500 to-accent-500 bg-clip-text text-transparent mb-4">
                        LL97 Fine Calculator
                    </h1>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                        Calculate your building's potential Local Law 97 fines and emissions compliance requirements 
                        with our advanced analysis tool
                    </p>
                    <div className="mt-6 flex justify-center">
                        <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-6 py-3 border border-gray-200 shadow-lg">
                            <span className="text-sm font-semibold text-gray-700">🏢 NYC Building Compliance • 📊 Real-time Analysis • ⚡ Instant Results</span>
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-br from-white via-slate-50 to-white rounded-3xl shadow-2xl border border-gray-200/50 backdrop-blur-sm overflow-hidden">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                        {/* Enhanced Input Section */}
                        <div className="p-10 border-r border-gray-200">
                            <div className="mb-8">
                                <h3 className="text-2xl font-bold text-gray-900 mb-2">Building Information</h3>
                                <p className="text-gray-600">Enter your property details for precise LL97 compliance analysis</p>
                            </div>
                            
                            <div className="space-y-8">
                                <div className="group">
                                    <label className="block text-sm font-bold text-gray-700 mb-3 uppercase tracking-wide">
                                        Building Type
                                    </label>
                                    <div className="relative">
                                        <select
                                            value={buildingType}
                                            onChange={(e) => setBuildingType(e.target.value)}
                                            className="w-full px-6 py-4 bg-white border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300 text-lg appearance-none cursor-pointer"
                                        >
                                            {buildingTypes.map(type => (
                                                <option key={type} value={type}>{type}</option>
                                            ))}
                                        </select>
                                        <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="group">
                                    <label className="block text-sm font-bold text-gray-700 mb-3 uppercase tracking-wide">
                                        Gross Floor Area (sq ft)
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            value={grossFloorArea}
                                            onChange={(e) => setGrossFloorArea(Number(e.target.value))}
                                            className="w-full px-6 py-4 bg-white border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300 text-lg"
                                            placeholder="100,000"
                                        />
                                        <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm font-medium">
                                            sq ft
                                        </div>
                                    </div>
                                    <div className="mt-2 text-xs text-gray-500">
                                        Total building floor area subject to LL97
                                    </div>
                                </div>
                                
                                <div className="group">
                                    <label className="block text-sm font-bold text-gray-700 mb-3 uppercase tracking-wide">
                                        Annual GHG Emissions (Metric Tons CO2e)
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            value={totalGHGEmissions}
                                            onChange={(e) => setTotalGHGEmissions(Number(e.target.value))}
                                            className="w-full px-6 py-4 bg-white border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300 text-lg"
                                            placeholder="500"
                                        />
                                        <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm font-medium">
                                            tCO2e
                                        </div>
                                    </div>
                                    <div className="mt-2 text-xs text-gray-500">
                                        Based on latest energy benchmarking report
                                    </div>
                                </div>
                                
                                <button
                                    onClick={handleCalculate}
                                    className="group w-full bg-gradient-to-r from-primary-600 to-secondary-600 text-white py-5 px-8 rounded-xl font-bold text-lg hover:from-primary-700 hover:to-secondary-700 focus:outline-none focus:ring-4 focus:ring-primary-500/20 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                                >
                                    <span className="flex items-center justify-center space-x-3">
                                        <svg className="w-6 h-6 group-hover:rotate-90 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                        </svg>
                                        <span>Calculate LL97 Fines</span>
                                    </span>
                                </button>
                            </div>
                        </div>
                        
                        {/* Enhanced Results Section */}
                        <div className="p-10 bg-gradient-to-br from-gray-50 to-white">
                            <div className="mb-8">
                                <h3 className="text-2xl font-bold text-gray-900 mb-2">LL97 Compliance Analysis</h3>
                                <p className="text-gray-600">Real-time fine calculations and compliance projections</p>
                            </div>
                            
                            {!currentCalculation ? (
                                <div className="text-center py-16">
                                    <div className="mb-6">
                                        <div className="mx-auto w-24 h-24 bg-gradient-to-br from-primary-100 to-secondary-100 rounded-full flex items-center justify-center">
                                            <svg className="w-12 h-12 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                            </svg>
                                        </div>
                                    </div>
                                    <h4 className="text-xl font-semibold text-gray-900 mb-3">Ready to Calculate</h4>
                                    <p className="text-gray-600 mb-6">
                                        Enter your building information and click calculate to see your LL97 fine projections
                                    </p>
                                    <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                                        <h5 className="font-semibold text-gray-900 mb-4">What you'll get:</h5>
                                        <div className="space-y-3 text-sm text-gray-600">
                                            <div className="flex items-center space-x-2">
                                                <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                                                <span>Current period (2025-2029) fine calculations</span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <div className="w-2 h-2 bg-secondary-500 rounded-full"></div>
                                                <span>Future period (2030+) fine projections</span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <div className="w-2 h-2 bg-accent-500 rounded-full"></div>
                                                <span>Emissions intensity analysis</span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <div className="w-2 h-2 bg-neutral-500 rounded-full"></div>
                                                <span>Compliance recommendations</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-8">
                                    {/* Current Period Results */}
                                    <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-lg">
                                        <div className="flex items-center justify-between mb-6">
                                            <h4 className="text-xl font-bold text-gray-900">Current Period (2025-2029)</h4>
                                            <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                                                Phase 1
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 gap-4">
                                            <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-xl">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm font-semibold text-blue-700">Emissions Intensity:</span>
                                                    <span className="text-lg font-bold text-blue-900">
                                                        {currentCalculation.emissionsIntensity.toFixed(2)} kgCO2e/sq ft
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-xl">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm font-semibold text-gray-700">Emissions Limit:</span>
                                                    <span className="text-lg font-bold text-gray-900">
                                                        {currentCalculation.applicableThreshold.toFixed(2)} kgCO2e/sq ft
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="bg-gradient-to-r from-red-50 to-red-100 p-4 rounded-xl">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm font-semibold text-red-700">Annual Fine:</span>
                                                    <span className="text-2xl font-black text-red-900">
                                                        ${currentCalculation.currentAnnualFine.toLocaleString()}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Future Period Results */}
                                    {futureCalculation && (
                                        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-lg">
                                            <div className="flex items-center justify-between mb-6">
                                                <h4 className="text-xl font-bold text-gray-900">Future Period (2030+)</h4>
                                                <div className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold">
                                                    Phase 2
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-1 gap-4">
                                                <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-xl">
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-sm font-semibold text-blue-700">Emissions Intensity:</span>
                                                        <span className="text-lg font-bold text-blue-900">
                                                            {futureCalculation.emissionsIntensity.toFixed(2)} kgCO2e/sq ft
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-xl">
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-sm font-semibold text-gray-700">Emissions Limit:</span>
                                                        <span className="text-lg font-bold text-gray-900">
                                                            {futureCalculation.applicableThreshold.toFixed(2)} kgCO2e/sq ft
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="bg-gradient-to-r from-red-50 to-red-100 p-4 rounded-xl">
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-sm font-semibold text-red-700">Annual Fine:</span>
                                                        <span className="text-2xl font-black text-red-900">
                                                            ${futureCalculation.projected2030Fine.toLocaleString()}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Professional Summary */}
                                    <div className="bg-gradient-to-r from-primary-50 via-secondary-50 to-accent-50 rounded-2xl p-6 border border-primary-200">
                                        <h5 className="text-lg font-bold text-gray-900 mb-4">Compliance Summary</h5>
                                        <div className="space-y-3">
                                            <div className="flex items-center space-x-3">
                                                {currentCalculation.currentAnnualFine > 0 ? (
                                                    <>
                                                        <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                                                        <span className="text-sm text-red-700 font-medium">
                                                            Building exceeds LL97 emissions limits
                                                        </span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                                        <span className="text-sm text-green-700 font-medium">
                                                            Building meets LL97 emissions limits
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                            {currentCalculation.currentAnnualFine > 0 && (
                                                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                                                    <p className="text-sm text-amber-800 font-medium">
                                                        💡 <strong>Recommendation:</strong> Consider energy efficiency retrofits to reduce emissions and avoid fines.
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Professional Footer with Additional Resources */}
                <div className="mt-12 text-center">
                    <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-gray-200 shadow-lg">
                        <h3 className="text-2xl font-bold text-gray-900 mb-4">Need Help with LL97 Compliance?</h3>
                        <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                            Our expert team can help you develop a comprehensive compliance strategy, 
                            connect with qualified contractors, and secure financing for energy efficiency improvements.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button className="bg-gradient-to-r from-primary-600 to-secondary-600 text-white px-8 py-3 rounded-xl font-semibold hover:from-primary-700 hover:to-secondary-700 transition-all duration-300 shadow-lg hover:shadow-xl">
                                Schedule Consultation
                            </button>
                            <button className="bg-white text-primary-600 border-2 border-primary-600 px-8 py-3 rounded-xl font-semibold hover:bg-primary-50 transition-all duration-300">
                                Download LL97 Guide
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LL97Calculator;